import styles from './VerifyBusiness.module.css';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  addCompanyInformation,
  getPresignedUrl,
  getTenantCompany,
  uploadToS3,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { AuthBrandedLoader } from '../../Login/AuthBrandedLoader';
import { ArrowUpFromLine, FileText, Trash } from 'lucide-react';
import { trackOnboardingStepCompleted } from '../../../helpers/utils/analytics';

const VerifyBusiness = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const formik = useFormik({
    initialValues: { documentPath: '' },
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        if (!values.documentPath) {
          setStatus('Please upload a document');
          setLoading(false);
          return;
        }

        const requestBody = { name: companyName || '', documentPath: values.documentPath };
        await addCompanyInformation(auth?.data?.token, requestBody);
        setStatus('');
        trackOnboardingStepCompleted('verify_business');
        navigate('/kyb/stakeholders');
      } catch (error) {
        console.error('Error Adding Company Information:', error);
        setStatus('Error submitting document');
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const { uploadUrl, url } = await getPresignedUrl(
        file.name,
        file.type,
        auth?.data.token,
        'kyb-document'
      );
      await uploadToS3(file, uploadUrl);

      setFileName(file.name);
      setSelectedFile(url);
      formik.setFieldValue('documentPath', url);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
    }
  };
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const companyInfoResponse = await getTenantCompany(auth?.data?.token);

        const company = companyInfoResponse?.data;

        // Set company name
        setCompanyName(company?.name || null);

        // If document already exists, show it
        if (company?.documentPath) {
          const existingUrl = company.documentPath;
          const existingFileName = existingUrl.split('/').pop() || 'Uploaded Document';

          setSelectedFile(existingUrl);
          setFileName(existingFileName);

          // Set formik value so user doesn't need to re-upload
          formik.setFieldValue('documentPath', existingUrl);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [auth?.data?.token]);

  if (loading) return <AuthBrandedLoader message='Loading…' />;

  return (
    <section>
      <motion.main className={`${styles.main} auth-onboarding-card`} initial={false}>
        <div className={styles.formOuter}>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <div className={styles.instructions}>
              <p>Please upload one of the following documents to verify your business entity:</p>
              <ul>
                <li>Certificate of Incorporation</li>
                <li>Articles of Incorporation</li>
                <li>Partnership Agreement</li>
                <li>Business Registration Certificate</li>
              </ul>
            </div>

            <div
              className={`${styles.uploadBox} ${selectedFile ? styles.uploadedFile : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              {!selectedFile && (
                <div className={styles.uploadContent}>
                  <ArrowUpFromLine size={36} stroke='var(--primary-color)' />
                  <p>Click or drag file to upload</p>
                  <span>PDF, DOC, or Image up to 10MB</span>
                </div>
              )}

              {selectedFile && (
                <div className={styles.uploadedContent}>
                  <FileText size={24} stroke='var(--primary-color)' />
                  <button
                    className={styles.fileName}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(selectedFile, '_blank');
                    }}
                  >
                    {fileName}
                  </button>
                  <button
                    className={styles.removeBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedFile('');
                      formik.setFieldValue('documentPath', '');
                    }}
                  >
                    <Trash size={20} stroke='#f1416c' />
                  </button>
                </div>
              )}
            </div>

            {isUploading && <div className={styles.uploadingText}>Uploading...</div>}
            {formik.status && <div className={styles.error}>{formik.status}</div>}

            <input
              ref={fileInputRef}
              type='file'
              hidden
              accept='image/*,.doc,.docx,.ppt,.pptx,.txt,.pdf'
              onChange={handleFileChange}
            />

            <div className='flex gap-4'>
              <button onClick={() => navigate('/kyb')} type='button' className='cancelBtn'>
                Back
              </button>
              <button
                type='submit'
                disabled={!formik.values.documentPath || formik.isSubmitting}
                className={`submitBtn ${
                  (!formik.values.documentPath || formik.isSubmitting) && styles.disableBtn
                }`}
              >
                Next
              </button>
            </div>
          </form>
        </div>
      </motion.main>
    </section>
  );
};

export default VerifyBusiness;
